import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import {
	map,
	tap,
	filter,
	withLatestFrom,
	concatMap,
	switchMap,
} from 'rxjs/operators';
import { DataPersistence } from '@nrwl/angular';
import { Router, ActivatedRouteSnapshot } from '@angular/router';
import { isEmpty } from 'lodash';
import { isMissingPagingArgs } from 'src/admin-app/utilities/list-filtering/shared';
import { addPagingListArgs } from 'src/shared/utilities/custom-rxjs-operators';
import { UserPreferencesService } from 'src/shared/services/user-preferences.service';
import { UserClient, UserRegistrationRequestClient } from 'src/shared/api';
import * as CustomerUsersActions from './customer-users.actions';
import * as CustomerUsersSelectors from './customer-users.selectors';
import { usersFragments } from '../../users.routes';
import {
	convertToUsersParams,
	convertToRegistrationRequestListArgs,
} from '../customer-users.utility';
import { CustomerUserListComponent } from '../customer-user-list/customer-user-list.component';
import {
	CustomerUsersState,
	CustomerUsersPartialState,
} from './customer-users.reducer';
import { convertToCustomersListArgs } from 'src/admin-app/customers/customer-list/customer-list.utility';
import { CustomerUserDetailComponent } from '../customer-user-detail/customer-user-detail.component';
import { RegistrationRequestListComponent } from '../registration-request-list/registration-request-list.component';
import { grabGuidFromParams } from 'src/shared/utilities/utility-functions';
import { Store, select } from '@ngrx/store';

@Injectable()
export class CustomerUsersEffects {
	updateListQueryParams$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerUsersActions.filtersUpdated),
				map(({ filters }) => convertToUsersParams(filters)),
				tap((queryParams) => {
					this.router.navigate(usersFragments.customerUserList, {
						queryParams,
					});
				})
			),
		{ dispatch: false }
	);

	addDefaultParams$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerUsersActions.navigatedWithoutAnyParams),
			map((action) => action.listArgs),
			map((listArgs) =>
				CustomerUsersActions.navigatedWithoutPagingParams({
					listArgs,
				})
			)
		)
	);

	addPagingListArgs$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerUsersActions.navigatedWithoutPagingParams),
			map((action) => action.listArgs),
			addPagingListArgs(this.userPreferences),
			map((filters) => CustomerUsersActions.filtersUpdated({ filters }))
		)
	);

	getCustomerUsers$ = createEffect(() =>
		this.dataPersistence.navigation(CustomerUserListComponent, {
			run: (r: ActivatedRouteSnapshot, _state: CustomerUsersState) => {
				const listArgs = convertToCustomersListArgs(r.queryParamMap);

				if (isEmpty(listArgs)) {
					return CustomerUsersActions.navigatedWithoutAnyParams({
						listArgs,
					});
				}

				if (isMissingPagingArgs(listArgs)) {
					return CustomerUsersActions.navigatedWithoutPagingParams({
						listArgs,
					});
				}

				return this.userClient.getUsers(listArgs).pipe(
					map((pagedCustomerUsers) =>
						CustomerUsersActions.pagedCustomerUsersLoaded({
							pagedCustomerUsers,
							listArgs,
						})
					)
				);
			},
			onError: (_a: ActivatedRouteSnapshot, error: any) => {
				throw new Error(error);
			},
		})
	);

	selectCustomerUser$ = createEffect(() =>
		this.dataPersistence.navigation(CustomerUserDetailComponent, {
			run: (r: ActivatedRouteSnapshot, _state: CustomerUsersState) => {
				const selectedUserId = grabGuidFromParams(r.paramMap);

				if (selectedUserId) {
					return CustomerUsersActions.customerUserSelected({
						selectedUserId,
					});
				} else {
					const registrationRequestId =
						+r.queryParamMap.get('registrationRequestId') || 0;

					if (registrationRequestId) {
						return CustomerUsersActions.registrationRequestSelected(
							{
								registrationRequestId,
							}
						);
					}
				}

				return CustomerUsersActions.errorLoadingCustomerUser({
					error: 'Can not get user customer',
				});
			},
			onError: (_a: ActivatedRouteSnapshot, error: any) => {
				throw new Error(error);
			},
		})
	);

	loadCustomerUserFromStore$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerUsersActions.customerUserSelected),
			map((action) => action.selectedUserId),
			filter(Boolean),
			withLatestFrom(
				this.store.pipe(
					select(CustomerUsersSelectors.getSelectedCustomerUser)
				),
				(_id, user) => user
			),
			filter(Boolean),
			map((user) =>
				CustomerUsersActions.customerUserLoadedFromStore({ user })
			)
		)
	);

	loadCustomerUserFromApi$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerUsersActions.customerUserSelected),
			map((action) => action.selectedUserId),
			filter<string>(Boolean),
			withLatestFrom(
				this.store.pipe(
					select(CustomerUsersSelectors.getSelectedCustomerUser)
				)
			),
			filter(([_id, user]) => !user),
			concatMap(([id, _user]) =>
				this.userClient.getUser(id).pipe(
					map((user) =>
						CustomerUsersActions.customerUserLoadedFromApi({
							user,
						})
					)
				)
			)
		)
	);

	saveCustomerUser$ = createEffect(() =>
		this.dataPersistence.pessimisticUpdate(
			CustomerUsersActions.saveRequested,
			{
				run: (
					action: ReturnType<
						typeof CustomerUsersActions.saveRequested
					>,
					_state: CustomerUsersState
				) => {
					const user = action.formValue;

					if (user.userRegistrationRequestId > 0 && !user.id) {
						return this.userClient.addPortalUser(user).pipe(
							map((portalUser) =>
								CustomerUsersActions.newCustomerUserSaved({
									user: { ...portalUser },
								})
							)
						);
					}

					return this.userClient.putUser(user).pipe(
						map((userResponse) =>
							user.id
								?
								CustomerUsersActions.customerUserSaved({
									user: { ...userResponse },
								}) :
								CustomerUsersActions.newCustomerUserSaved({
									user: { ...userResponse },
								})
						)
					);
				},
				onError: (
					_action: ReturnType<
						typeof CustomerUsersActions.saveRequested
					>,
					error: any
				) => {
					return CustomerUsersActions.errorSavingCustomerUser({
						error,
					});
				},
			}
		)
	);

	throwIfError$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					CustomerUsersActions.errorSavingCustomerUser,
					CustomerUsersActions.errorLoadingCustomerUser
				),
				map((action) => action.error),
				map((error) => {
					throw Error(error);
				})
			),
		{ dispatch: false }
	);

	closeAfterSaving$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerUsersActions.customerUserSaved),
				withLatestFrom(
					this.store.pipe(
						select(CustomerUsersSelectors.getCloseAfterSaving)
					),
					(_, shouldClose) => shouldClose
				),
				filter(Boolean),
				tap(() => this.router.navigate(usersFragments.customerUserList))
			),
		{ dispatch: false }
	);

	navigateToCreatedCustomerUser$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerUsersActions.newCustomerUserSaved),
				map((action) => action.user.id),
				tap((id) =>
					this.router.navigate(usersFragments.customerUserDetail(id))
				)
			),
		{ dispatch: false }
	);

	onRegistrationRequestsPageLoaded$ = createEffect(() =>
		this.dataPersistence.navigation(RegistrationRequestListComponent, {
			run: (r: ActivatedRouteSnapshot, _state: CustomerUsersState) => {
				const listArgs = convertToRegistrationRequestListArgs(
					r.queryParamMap
				);

				return CustomerUsersActions.pagedRegistrationRequestsRequested({
					listArgs,
				});
			},
			onError: (_a: ActivatedRouteSnapshot, error: any) => {
				throw new Error(error);
			},
		})
	);

	getRegistrationRequests$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerUsersActions.pagedRegistrationRequestsRequested),
			switchMap(({ listArgs }) =>
				this.userRegistrationRequestClient
					.userRegistrationRequests(listArgs)
					.pipe(
						map((pagedRegistrationRequests) =>
							CustomerUsersActions.pagedRegistrationRequestsLoaded(
								{
									pagedRegistrationRequests,
								}
							)
						)
					)
			)
		)
	);

	denyRegistrationRequest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerUsersActions.registrationRequestDenied),
			switchMap(({ id }) =>
				this.userRegistrationRequestClient
					.denyUserRegistrationRequest(id)
					.pipe(
						map((registrationRequest) => CustomerUsersActions.registrationRequestDenyCompleted({ registrationRequest }))
					)
			)
		)
	);

	getSelectedRegistrationRequestFromApi$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerUsersActions.registrationRequestSelected),
			map((action) => action.registrationRequestId),
			filter<number>(Boolean),
			switchMap((registrationRequestId) =>
				this.userRegistrationRequestClient
					.getRegistrationRequest(registrationRequestId)
					.pipe(
						map((potentialUser) =>
							CustomerUsersActions.customerUserLoadedFromApi({
								user: {
									...potentialUser,
									id: ''
								},
							})
						)
					)
			)
		)
	);

	constructor(
		private readonly actions$: Actions,
		private readonly dataPersistence: DataPersistence<CustomerUsersState>,
		private readonly store: Store<CustomerUsersPartialState>,
		private readonly userPreferences: UserPreferencesService,
		private readonly userClient: UserClient,
		private readonly userRegistrationRequestClient: UserRegistrationRequestClient,
		private readonly router: Router
	) { }
}
